import { supabase } from './supabaseClient';
import { serviceRoleClient } from './serviceRoleClient';
import { LATEST_STAFF_APP_VERSION } from '../constants/appRelease';
import { applyOpenJobFilter, countExact } from '../components/admin/PartnerActivityFeed';
import { ENV, SUPABASE_ANON_KEY, SUPABASE_URL } from '../config/env';

// Export supabase for backward compatibility with admin pages
export { supabase };

const EMPTY_MONEY = { partnerAdvance: 0, partnerRepayCollected: 0, ticketRazorpayRevenue: 0 };

const EMPTY_ADMIN_STATS = {
  totalUsers: 0,
  totalCustomers: 0,
  totalMechanics: 0,
  activeRequests: 0,
  totalRevenue: 0,
  pendingOrders: 0,
  supportTickets: 0,
  totalLeads: 0,
  totalMasterOrders: 0,
  pendingAssignments: 0,
  completedServices: 0,
  todayRequests: 0,
  deliveredLeads: 0,
  uniqueMechanicPincodes: 0,
  ...EMPTY_MONEY,
};

async function settle(promise, fallback, ms = 8000) {
  let timer;
  try {
    return await Promise.race([
      promise,
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error('timeout')), ms);
      }),
    ]);
  } catch {
    return fallback;
  } finally {
    clearTimeout(timer);
  }
}

function restHeaders() {
  const headers = {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    'Content-Type': 'application/json',
  };
  try {
    const raw = localStorage.getItem('roadserve_sb_session');
    const backup = raw ? JSON.parse(raw) : null;
    if (backup?.access_token) {
      headers.Authorization = `Bearer ${backup.access_token}`;
    }
  } catch {
    // keep anon
  }
  return headers;
}

async function fetchRpc(name, body = {}, ms = 10000) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  try {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${name}`, {
      method: 'POST',
      headers: restHeaders(),
      body: JSON.stringify(body ?? {}),
      signal: ctrl.signal,
    });
    if (!res.ok) {
      return { data: null, error: { message: `${name} ${res.status}` } };
    }
    const data = await res.json();
    return { data, error: null };
  } catch (error) {
    return { data: null, error: { message: error?.message || `${name} failed` } };
  } finally {
    clearTimeout(timer);
  }
}

// Admin Service - Web version
export class AdminService {
  static ticketRazorpayAmount(row) {
    return Number(row?.total_amount || row?.amount_inr || 0);
  }

  static repayRazorpayAmount(row) {
    if (!row) return 0;
    const total = Number(row.total_amount);
    if (Number.isFinite(total) && total > 0) return total;
    return Number(row.amount || 0) + Number(row.gst_amount || 0);
  }

  static isRazorpayTicketPurchase(row) {
    if (!row || String(row.status || '').toLowerCase() !== 'paid') return false;
    if (row.paid_with_credits === true) return false;
    return Boolean(String(row.razorpay_payment_id || '').trim());
  }

  static async listPartnerTicketRazorpayPurchases(limit = 200) {
    const cap = Math.max(1, Math.min(Number(limit) || 200, 10000));
    const attempts = [
      'id, mechanic_id, amount_inr, total_amount, status, razorpay_payment_id, paid_with_credits, paid_at, created_at',
      'id, mechanic_id, amount_inr, total_amount, status, razorpay_payment_id, paid_at, created_at',
      'id, mechanic_id, amount_inr, status, razorpay_payment_id, paid_at, created_at',
    ];
    let lastError = null;
    for (const cols of attempts) {
      const { data, error } = await supabase
        .from('partner_ticket_purchases')
        .select(cols)
        .eq('status', 'paid')
        .not('razorpay_payment_id', 'is', null)
        .order('paid_at', { ascending: false, nullsFirst: false })
        .limit(cap);
      if (!error) {
        return (data || []).filter((row) => AdminService.isRazorpayTicketPurchase(row));
      }
      lastError = error;
      if (!/total_amount|paid_with_credits|column/i.test(error.message || '')) break;
    }
    if (lastError) throw lastError;
    return [];
  }

  static emptyAdminStats() {
    return { ...EMPTY_ADMIN_STATS };
  }

  static async getPartnerMoneyStats() {
    return settle(AdminService._getPartnerMoneyStats(), { ...EMPTY_MONEY }, 10000);
  }

  static async _getPartnerMoneyStats() {
    try {
      const [grantsRes, repayRes, ticketRows] = await Promise.all([
        supabase.from('partner_credit_grants').select('amount').eq('repayable', true).limit(5000),
        supabase
          .from('partner_credit_repayments')
          .select('amount, method, gst_amount, total_amount')
          .eq('status', 'paid')
          .in('method', ['razorpay', 'cash'])
          .limit(5000),
        AdminService.listPartnerTicketRazorpayPurchases(5000).catch((e) => {
          console.error('Error listing ticket Razorpay purchases:', e);
          return [];
        }),
      ]);
      const partnerAdvance = (grantsRes.data || []).reduce(
        (sum, row) => sum + Number(row.amount || 0),
        0,
      );
      const partnerRepayCollected = (repayRes.data || []).reduce(
        (sum, row) => sum + AdminService.repayRazorpayAmount(row),
        0,
      );
      const ticketRazorpayRevenue = (ticketRows || []).reduce(
        (sum, row) => sum + AdminService.ticketRazorpayAmount(row),
        0,
      );
      return { partnerAdvance, partnerRepayCollected, ticketRazorpayRevenue };
    } catch (e) {
      console.error('Error getting partner money stats:', e);
      return { ...EMPTY_MONEY };
    }
  }

  static async fetchAdminStatsRpc() {
    return fetchRpc('admin_get_stats', {});
  }

  static async getAdminStats() {
    try {
      const [{ data, error }, openComplaints] = await Promise.all([
        AdminService.fetchAdminStatsRpc(),
        settle(countExact('service_requests', applyOpenJobFilter).catch(() => 0), 0, 6000),
      ]);

      if (error) {
        console.error('Failed to get admin stats:', error);
        return {
          ...EMPTY_ADMIN_STATS,
          activeRequests: Number(openComplaints) || 0,
        };
      }

      const statsRow = Array.isArray(data) ? data[0] : data;
      if (!statsRow) {
        return {
          ...EMPTY_ADMIN_STATS,
          activeRequests: Number(openComplaints) || 0,
        };
      }

      return {
        ...EMPTY_ADMIN_STATS,
        totalUsers: Number(statsRow.total_users) || 0,
        totalCustomers: Number(statsRow.active_users) || 0,
        activeRequests: Number(openComplaints) || 0,
        totalRevenue: Number(statsRow.total_revenue) || 0,
        pendingOrders: Number(statsRow.pending_orders) || 0,
        supportTickets: Number(statsRow.support_tickets) || 0,
        totalLeads: Number(statsRow.total_leads) || 0,
        totalMasterOrders: Number(statsRow.total_master_orders) || 0,
        pendingAssignments: Number(statsRow.pending_assignments) || 0,
        completedServices: Number(statsRow.completed_services) || 0,
        deliveredLeads: Number(statsRow.delivered_leads) || 0,
        uniqueMechanicPincodes: Number(statsRow.unique_mechanic_pincodes) || 0,
      };
    } catch (error) {
      console.error('Error getting admin stats:', error);
      return { ...EMPTY_ADMIN_STATS };
    }
  }

  static normalizeServiceTypeLabel(raw) {
    const s = String(raw || '').trim();
    if (!s) return 'Unknown';
    const lower = s.toLowerCase();
    if (lower.startsWith('garage')) return 'Garage';
    if (lower.includes('tow') || lower.includes('tochan') || lower.includes('recovery')) return 'Towing';
    if (lower.includes('jump') || lower.includes('battery')) return 'Jump Start';
    if (lower.includes('tyre') || lower.includes('tire') || lower.includes('puncture') || lower.includes('flat')) {
      return 'Flat Tyre';
    }
    if (lower.includes('fuel') || lower.includes('petrol') || lower.includes('diesel')) return 'Fuel Delivery';
    if (lower.includes('lock') || lower.includes('key')) return 'Key Lockout';
    if (lower.includes('minor')) return 'Minor Repair';
    if (lower.includes('emergency') || lower === 'rsa' || lower.includes('roadside')) return 'RSA';
    const head = s.split(/[-–—|/]/)[0].trim();
    return head || 'Other';
  }

  static jobDateYmdIst(row) {
    const iso = row.complaint_registered_at || row.requested_at || row.updated_at;
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  }

  static pieCustomerKey(row) {
    const digits = String(row.contact_number || '').replace(/\D/g, '');
    if (digits.length >= 10) return `p:${digits.slice(-10)}`;
    if (row.customer_id) return `u:${row.customer_id}`;
    return `id:${row.id}`;
  }

  static pieJobAtMs(row) {
    const iso = row.complaint_registered_at || row.requested_at || row.updated_at;
    const t = iso ? new Date(iso).getTime() : 0;
    return Number.isFinite(t) ? t : 0;
  }

  static complaintStatusBucket(row) {
    const req = String(row.request_status || '').toLowerCase();
    const comp = String(row.complaint_status || '').toLowerCase();
    if (
      row.mechanic_completed_datetime ||
      req === 'completed' ||
      req === 'complete' ||
      comp === 'completed' ||
      comp === 'complete'
    ) {
      return 'complete';
    }
    if (
      req === 'cancelled' ||
      req === 'canceled' ||
      req === 'closed' ||
      comp === 'cancelled' ||
      comp === 'canceled' ||
      comp === 'closed' ||
      comp === 'rejected'
    ) {
      return 'cancel';
    }
    return 'open';
  }

  static emptyStatusCounts() {
    return { complete: 0, cancel: 0, open: 0 };
  }

  static parseStatusCounts(rows) {
    const counts = AdminService.emptyStatusCounts();
    (rows || []).forEach((row) => {
      const key = String(row.status_bucket || row.bucket || '').toLowerCase();
      if (key === 'complete' || key === 'completed') counts.complete += Number(row.job_count ?? row.count ?? 0);
      else if (key === 'cancel' || key === 'cancelled' || key === 'canceled') {
        counts.cancel += Number(row.job_count ?? row.count ?? 0);
      } else if (key === 'open') counts.open += Number(row.job_count ?? row.count ?? 0);
    });
    return counts;
  }

  static pieRangeIso(fromYmd, toYmd) {
    const fromIso = fromYmd ? new Date(`${fromYmd}T00:00:00+05:30`).toISOString() : '';
    const toIso = toYmd ? new Date(`${toYmd}T23:59:59.999+05:30`).toISOString() : '';
    return { fromIso, toIso };
  }

  static tallyUniqueCustomerDay(rows, fromYmd, toYmd) {
    const tallies = new Map();
    (rows || []).forEach((row) => {
      const ymd = AdminService.jobDateYmdIst(row);
      if (!ymd) return;
      if (fromYmd && ymd < fromYmd) return;
      if (toYmd && ymd > toYmd) return;
      const dupKey = `${AdminService.pieCustomerKey(row)}|${ymd}`;
      const at = AdminService.pieJobAtMs(row);
      const prev = tallies.get(dupKey);
      if (!prev || at >= prev.at) {
        tallies.set(dupKey, {
          service_type: String(row.service_type || '').trim() || 'Unknown',
          status: AdminService.complaintStatusBucket(row),
          at,
        });
      }
    });
    return tallies;
  }

  static async fetchPieSourceRows(fromYmd, toYmd) {
    const { fromIso, toIso } = AdminService.pieRangeIso(fromYmd, toYmd);
    const pageSize = 1000;
    const bounded = Boolean(fromYmd && toYmd);
    const maxPages = bounded ? 20 : 8;
    const seen = new Set();
    const rows = [];
    const timeCols = bounded ? ['requested_at', 'complaint_registered_at'] : ['requested_at'];
    for (const col of timeCols) {
      for (let i = 0; i < maxPages; i += 1) {
        const fromIdx = i * pageSize;
        let q = supabase
          .from('service_requests')
          .select(
            'id, customer_id, contact_number, service_type, request_status, complaint_status, mechanic_completed_datetime, complaint_registered_at, requested_at, updated_at',
          )
          .or('delete_status.is.null,delete_status.eq.false')
          .order(col, { ascending: false, nullsFirst: false })
          .range(fromIdx, fromIdx + pageSize - 1);
        if (fromIso) q = q.gte(col, fromIso);
        if (toIso) q = q.lte(col, toIso);
        const { data, error } = await q;
        if (error) throw error;
        const batch = data || [];
        batch.forEach((row) => {
          const id = String(row.id || '');
          if (!id || seen.has(id)) return;
          seen.add(id);
          rows.push(row);
        });
        if (batch.length < pageSize) break;
      }
    }
    return rows;
  }

  static slicesFromTypeRows(rawRows) {
    const grouped = new Map();
    (rawRows || []).forEach((row) => {
      const label = AdminService.normalizeServiceTypeLabel(row.service_type);
      grouped.set(label, (grouped.get(label) || 0) + Number(row.count || 0));
    });
    const slices = [...grouped.entries()]
      .map(([name, value]) => ({ name, value }))
      .filter((row) => row.value > 0)
      .sort((a, b) => b.value - a.value);
    const total = slices.reduce((sum, row) => sum + row.value, 0);
    return { slices, total };
  }

  static breakdownFromTallies(tallies) {
    const typeCounts = new Map();
    const statuses = AdminService.emptyStatusCounts();
    tallies.forEach((row) => {
      typeCounts.set(row.service_type, (typeCounts.get(row.service_type) || 0) + 1);
      if (statuses[row.status] != null) statuses[row.status] += 1;
    });
    const rawRows = [...typeCounts.entries()].map(([service_type, count]) => ({ service_type, count }));
    const { slices, total } = AdminService.slicesFromTypeRows(rawRows);
    return { slices, total, statuses };
  }

  static async getServiceTypeBreakdown({ from = '', to = '' } = {}) {
    const empty = { total: 0, slices: [], statuses: AdminService.emptyStatusCounts() };
    const fromYmd = String(from || '').trim();
    const toYmd = String(to || '').trim();
    const rpcParams = { p_from: fromYmd || null, p_to: toYmd || null };
    try {
      const [typeRes, statusRes] = await Promise.all([
        fetchRpc('admin_service_type_counts', rpcParams, 8000),
        fetchRpc('admin_complaint_status_counts', rpcParams, 8000),
      ]);
      const typeOk = !typeRes.error && Array.isArray(typeRes.data);
      const statusOk = !statusRes.error && Array.isArray(statusRes.data);

      let rawRows = [];
      let statuses = AdminService.emptyStatusCounts();

      if (typeOk) {
        rawRows = typeRes.data.map((row) => ({
          service_type: row.service_type,
          count: Number(row.job_count ?? row.count ?? 0),
        }));
      }
      if (statusOk) statuses = AdminService.parseStatusCounts(statusRes.data);

      const statusTotal = statuses.complete + statuses.cancel + statuses.open;
      const { slices, total } = AdminService.slicesFromTypeRows(rawRows);
      const needFallback = !typeOk || !statusOk || (total > 0 && statusTotal === 0);

      if (needFallback) {
        if (!typeOk) console.warn('admin_service_type_counts unavailable', typeRes.error?.message);
        if (!statusOk) console.warn('admin_complaint_status_counts unavailable', statusRes.error?.message);
        try {
          const tallies = AdminService.tallyUniqueCustomerDay(
            await AdminService.fetchPieSourceRows(fromYmd, toYmd),
            fromYmd,
            toYmd,
          );
          const fallback = AdminService.breakdownFromTallies(tallies);
          if (!typeOk && fallback.slices.length) {
            return fallback;
          }
          if (fallback.statuses.complete + fallback.statuses.cancel + fallback.statuses.open > 0) {
            return {
              total: total || fallback.total,
              slices: slices.length ? slices : fallback.slices,
              statuses: fallback.statuses,
            };
          }
        } catch (fallbackError) {
          console.warn('service type fallback failed', fallbackError?.message || fallbackError);
        }
      }

      return { total, slices, statuses };
    } catch (error) {
      console.error('Error getting service type breakdown:', error);
      return empty;
    }
  }

  static async getSystemStatus() {
    try {
      const { error: dbError } = await settle(
        supabase.from('users').select('id').limit(1),
        { error: { message: 'timeout' } },
        5000,
      );
      const database = dbError ? 'offline' : 'online';
      return {
        database,
        payments: 'online',
        notifications: 'online',
        chat: 'online',
      };
    } catch (error) {
      return {
        database: 'offline',
        payments: 'offline',
        notifications: 'offline',
        chat: 'offline',
      };
    }
  }

  static mapAdminUser(user) {
    return {
      id: user.id,
      name: user.name,
      email: user.email,
      phone: user.phone,
      roles: user.roles || [],
      status: user.status || 'active',
      created_at: user.created_at,
      last_login: user.last_login,
      team_lead_id: user.team_lead_id,
      team_lead_name: user.team_lead_name,
      avatar_url: user.avatar_url || null,
      dialer_user_id: user.dialer_user_id || null,
      app_version: user.app_version,
      app_build_number: user.app_build_number,
      app_platform: user.app_platform,
      app_flavor: user.app_flavor,
      app_version_reported_at: user.app_version_reported_at,
      plan_name: user.plan_name || null,
      membership_status: user.membership_status || null,
      vehicle_numbers: AdminService.formatVehicleNumbers(user.vehicle_numbers),
    };
  }

  static normalizeVehiclePlate(value) {
    return String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  static formatVehicleNumbers(value) {
    if (Array.isArray(value)) {
      const plates = value.map((item) => String(item || '').trim()).filter(Boolean);
      return plates.length ? plates.join(', ') : null;
    }
    const text = String(value || '').trim();
    return text || null;
  }

  static looksLikeVehicleSearch(search) {
    const compact = AdminService.normalizeVehiclePlate(search);
    return compact.length >= 3 && /\d/.test(compact);
  }

  static async findUsersByVehicleNumber(search) {
    const empty = { ids: [], platesByUser: {} };
    if (!AdminService.looksLikeVehicleSearch(search)) return empty;

    const compact = AdminService.normalizeVehiclePlate(search);
    const likeValue = compact.replace(/[%_,.()]/g, '');
    if (likeValue.length < 3) return empty;

    try {
      const client = serviceRoleClient;
      const suffix = compact.length >= 6 ? compact.slice(-4) : likeValue;
      const [vehiclesRes, customersRes, vehiclesSuffixRes, customersSuffixRes] = await Promise.all([
        client
          .from('customer_vehicles')
          .select('customer_id, registration_number')
          .ilike('registration_number', `%${likeValue}%`)
          .limit(300),
        client
          .from('customers')
          .select('user_id, car_number')
          .ilike('car_number', `%${likeValue}%`)
          .limit(300),
        suffix !== likeValue
          ? client
              .from('customer_vehicles')
              .select('customer_id, registration_number')
              .ilike('registration_number', `%${suffix}%`)
              .limit(400)
          : Promise.resolve({ data: [] }),
        suffix !== likeValue
          ? client
              .from('customers')
              .select('user_id, car_number')
              .ilike('car_number', `%${suffix}%`)
              .limit(400)
          : Promise.resolve({ data: [] }),
      ]);

      const platesByUser = {};
      const addPlate = (userId, plate) => {
        if (!userId || !plate) return;
        const normalized = AdminService.normalizeVehiclePlate(plate);
        if (!normalized.includes(compact)) return;
        const key = String(userId);
        const label = String(plate).trim().toUpperCase();
        if (!platesByUser[key]) platesByUser[key] = new Set();
        platesByUser[key].add(label);
      };

      [...(vehiclesRes.data || []), ...(vehiclesSuffixRes.data || [])]
        .forEach((row) => addPlate(row.customer_id, row.registration_number));
      [...(customersRes.data || []), ...(customersSuffixRes.data || [])]
        .forEach((row) => addPlate(row.user_id, row.car_number));

      const ids = Object.keys(platesByUser);
      const mapped = {};
      ids.forEach((id) => {
        mapped[id] = [...platesByUser[id]].join(', ');
      });
      return { ids, platesByUser: mapped };
    } catch (error) {
      console.warn('findUsersByVehicleNumber:', error);
      return empty;
    }
  }

  static async fetchAdminUsersByIds(ids) {
    const unique = [...new Set((ids || []).filter(Boolean).map(String))];
    if (!unique.length) return [];

    try {
      const client = serviceRoleClient;
      const [{ data: users, error: usersError }, { data: roleRows }] = await Promise.all([
        client
          .from('users')
          .select('id, name, email, phone, status, created_at, last_login, avatar_url, app_version, app_build_number, app_platform, app_flavor, app_version_reported_at')
          .in('id', unique),
        client.from('user_roles').select('user_id, roles(name)').in('user_id', unique),
      ]);

      if (usersError) {
        console.warn('fetchAdminUsersByIds:', usersError);
        return [];
      }

      const rolesByUser = {};
      (roleRows || []).forEach((row) => {
        const roleName = row.roles?.name;
        if (!roleName || !row.user_id) return;
        if (!rolesByUser[row.user_id]) rolesByUser[row.user_id] = [];
        rolesByUser[row.user_id].push(roleName);
      });

      return (users || []).map((user) => AdminService.mapAdminUser({
        ...user,
        roles: rolesByUser[user.id] || [],
      }));
    } catch (error) {
      console.warn('fetchAdminUsersByIds:', error);
      return [];
    }
  }

  static filterUsersLocally(users, {
    search = '',
    role = 'all',
    status = 'all',
    appVersion = 'all',
    vehicleUserIds = [],
  } = {}) {
    const query = search.trim().toLowerCase();
    const plate = AdminService.normalizeVehiclePlate(search);
    const vehicleIdSet = new Set((vehicleUserIds || []).map(String));
    return (users || []).filter((user) => {
      const matchesSearch =
        !query ||
        user.name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        user.phone?.includes(search.trim()) ||
        (plate.length >= 3 && AdminService.normalizeVehiclePlate(user.vehicle_numbers).includes(plate)) ||
        vehicleIdSet.has(String(user.id));

      const matchesRole =
        role === 'all' ||
        user.roles?.some((r) => r?.toLowerCase() === role.toLowerCase());

      const matchesStatus =
        status === 'all' ||
        (user.status || 'active').toLowerCase() === status.toLowerCase();

      const matchesAppVersion = (() => {
        if (appVersion === 'all') return true;
        if (appVersion === 'not_reported') return !user.app_version;
        if (appVersion === 'latest') return user.app_version === LATEST_STAFF_APP_VERSION;
        if (appVersion === 'outdated') {
          return Boolean(user.app_version) && user.app_version !== LATEST_STAFF_APP_VERSION;
        }
        return user.app_version === appVersion;
      })();

      return matchesSearch && matchesRole && matchesStatus && matchesAppVersion;
    });
  }

  static async listUsers({
    search = '',
    role = 'all',
    status = 'all',
    appVersion = 'all',
    page = 1,
    pageSize = 20,
  } = {}) {
    const emptyResult = {
      items: [],
      total: 0,
      page: 1,
      pageSize,
      totalPages: 1,
      stats: { total_users: 0, active_users: 0, inactive_users: 0, filtered_total: 0 },
      appVersions: [],
      usedFallback: false,
    };

    try {
      const { data, error } = await supabase.rpc('admin_list_users', {
        p_search: search,
        p_role: role,
        p_status: status,
        p_app_version: appVersion,
        p_latest_version: LATEST_STAFF_APP_VERSION,
        p_page: page,
        p_page_size: pageSize,
      });

      if (error) {
        const msg = String(error.message || '').toLowerCase();
        const rpcMissing =
          error.code === 'PGRST202' ||
          error.code === '42883' ||
          msg.includes('admin_list_users') ||
          msg.includes('could not find the function') ||
          msg.includes('404');

        if (!rpcMissing) {
          console.error('Error listing users:', error);
          return emptyResult;
        }

        console.warn('admin_list_users RPC missing, using fallback loader');
        const allUsers = (await AdminService.getAllUsers()).map(AdminService.mapAdminUser);
        const vehicleHits = await AdminService.findUsersByVehicleNumber(search);
        const filtered = AdminService.filterUsersLocally(allUsers, {
          search,
          role,
          status,
          appVersion,
          vehicleUserIds: vehicleHits.ids,
        }).map((user) => ({
          ...user,
          vehicle_numbers: user.vehicle_numbers || vehicleHits.platesByUser[String(user.id)] || null,
        }));
        const total = filtered.length;
        const totalPages = Math.max(1, Math.ceil(total / pageSize));
        const safePage = Math.min(Math.max(page, 1), totalPages);
        const start = (safePage - 1) * pageSize;
        const items = filtered.slice(start, start + pageSize);
        const appVersions = Array.from(
          new Set(allUsers.map((u) => u.app_version).filter(Boolean)),
        ).sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));

        return {
          items,
          total,
          page: safePage,
          pageSize,
          totalPages,
          stats: {
            total_users: allUsers.length,
            active_users: allUsers.filter((u) => (u.status || 'active') === 'active').length,
            inactive_users: allUsers.filter((u) => (u.status || 'active') !== 'active').length,
            filtered_total: total,
          },
          appVersions,
          usedFallback: true,
        };
      }

      let items = Array.isArray(data?.items) ? data.items.map(AdminService.mapAdminUser) : [];
      let total = Number(data?.total || 0);
      const currentPage = Number(data?.page || page || 1);

      if (AdminService.looksLikeVehicleSearch(search)) {
        const vehicleHits = await AdminService.findUsersByVehicleNumber(search);
        items = items.map((user) => ({
          ...user,
          vehicle_numbers: user.vehicle_numbers || vehicleHits.platesByUser[String(user.id)] || null,
        }));

        const presentIds = new Set(items.map((user) => String(user.id)));
        const missingIds = vehicleHits.ids.filter((id) => !presentIds.has(String(id)));
        if (missingIds.length && currentPage === 1) {
          const extraUsers = (await AdminService.fetchAdminUsersByIds(missingIds)).map((user) => ({
            ...user,
            vehicle_numbers: vehicleHits.platesByUser[String(user.id)] || user.vehicle_numbers,
          }));
          if (extraUsers.length) {
            items = [...extraUsers, ...items];
            total += extraUsers.length;
          }
        }
      }

      const filteredTotal = Math.max(Number(data?.stats?.filtered_total || 0), total);

      return {
        items,
        total,
        page: currentPage,
        pageSize: Number(data?.page_size || pageSize),
        totalPages: Math.max(Math.ceil(total / Number(data?.page_size || pageSize)) || Number(data?.total_pages || 1), 1),
        stats: {
          total_users: Number(data?.stats?.total_users || 0),
          active_users: Number(data?.stats?.active_users || 0),
          inactive_users: Number(data?.stats?.inactive_users || 0),
          filtered_total: filteredTotal,
        },
        appVersions: Array.isArray(data?.app_versions) ? data.app_versions : [],
        usedFallback: false,
      };
    } catch (error) {
      console.error('Error listing users:', error);
      return emptyResult;
    }
  }

  static async getTeamLeads({ status = 'all' } = {}) {
    const result = await AdminService.listUsers({
      role: 'teamlead',
      status,
      page: 1,
      pageSize: 500,
    });
    return (result.items || []).map(AdminService.mapAdminUser);
  }

  static async getTelecallers({ status = 'all' } = {}) {
    const result = await AdminService.listUsers({
      role: 'telecaller',
      status,
      page: 1,
      pageSize: 1000,
    });
    return (result.items || []).map(AdminService.mapAdminUser);
  }

  static async getStaffDirectory() {
    const [teamLeads, telecallers] = await Promise.all([
      AdminService.getTeamLeads(),
      AdminService.getTelecallers(),
    ]);
    const byId = new Map();
    [...teamLeads, ...telecallers].forEach((user) => {
      byId.set(String(user.id), user);
    });
    return { teamLeads, telecallers, byId };
  }

  static async getAllUsers() {
    try {
      const { data, error } = await supabase.rpc('admin_get_all_users_all');
      if (error) {
        console.error('Error getting users:', error);
        return [];
      }
      return data?.map(user => ({
        id: user.id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        roles: user.roles || [],
        status: user.status || 'active',
        created_at: user.created_at,
        last_login: user.last_login,
        team_lead_id: user.team_lead_id,
        team_lead_name: user.team_lead_name,
        avatar_url: user.avatar_url || null,
        app_version: user.app_version,
        app_build_number: user.app_build_number,
        app_platform: user.app_platform,
        app_flavor: user.app_flavor,
        app_version_reported_at: user.app_version_reported_at,
      })) || [];
    } catch (error) {
      console.error('Error getting users:', error);
      return [];
    }
  }

  // Telecaller KYC list (joined view)
  static async getTelecallerKyc() {
    try {
      const { data, error } = await supabase.rpc('admin_list_telecaller_kyc');
      if (error) {
        console.error('Error getting telecaller KYC:', error);
        return [];
      }
      return data || [];
    } catch (error) {
      console.error('Error getting telecaller KYC:', error);
      return [];
    }
  }

  // Update telecaller KYC admin fields (verified + commission)
  static async updateTelecallerKyc(userId, { is_verified, commission_percent }) {
    try {
      const { data, error } = await supabase.rpc('admin_update_telecaller_kyc', {
        p_user_id: userId,
        p_is_verified: typeof is_verified === 'boolean' ? is_verified : null,
        p_commission_percent: commission_percent === '' || commission_percent === null || commission_percent === undefined
          ? null
          : Number(commission_percent),
      });
      if (error) throw error;
      return { success: true, data: data?.[0] || null };
    } catch (error) {
      console.error('Error updating telecaller KYC:', error);
      return { success: false, error: error.message || 'Failed to update KYC' };
    }
  }

  static async createUser(userData) {
    try {
      const primaryRole = userData.roles?.[0];
      if (!primaryRole) {
        return { success: false, error: 'At least one role is required' };
      }

      let assignedBy = userData.teamLeaderId || null;
      if (!assignedBy) {
        try {
          const currentUser = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
          assignedBy = currentUser?.id || null;
        } catch {
          assignedBy = null;
        }
      }

      // Plain password → admin_create_user_with_role RPC hashes with bcrypt on the server.
      const { data, error } = await supabase.rpc('admin_create_user_with_role', {
        p_name: userData.name,
        p_email: userData.email,
        p_phone: userData.phone || '',
        p_password: userData.password,
        p_role_name: primaryRole,
        p_status: userData.status || 'active',
        p_assigned_by: assignedBy,
        p_dialer_user_id: userData.dialerId || null,
      });

      if (error) throw error;

      const userId = data?.[0]?.id;
      if (!userId) throw new Error('Failed to get user ID');

      if (primaryRole === 'telecaller' && userData.teamLeaderId) {
        await this.changeTeamLead(userId, userData.teamLeaderId, assignedBy);
      }

      return { success: true, user: data[0] };
    } catch (error) {
      console.error('Error creating user:', error);
      return { success: false, error: error.message };
    }
  }

  static async updateUser(userId, userData) {
    try {
      const { data: updated, error: updErr } = await supabase.rpc('admin_update_user', {
        p_user_id: userId,
        p_name: userData.name || null,
        p_email: userData.email || null,
        p_phone: userData.phone || null,
        p_status: userData.status || null,
        p_dialer_user_id: userData.dialer_user_id || null
      });
      if (updErr) throw updErr;
      const ok = Array.isArray(updated)
        ? updated.some((r) => r?.success === true)
        : updated === true || updated?.success === true;
      if (!ok) throw new Error('User row did not update');

      // Update roles if provided
      if (userData.roles && userData.roles.length > 0) {
        // Delete existing roles
        const { error: delRolesError } = await supabase.rpc('admin_delete_user_roles', {
          p_user_id: userId
        });
        if (delRolesError) throw delRolesError;

        // Get new role IDs
        const { data: rolesData } = await supabase.rpc('admin_get_roles_by_names', {
          role_names: userData.roles
        });

        if (rolesData) {
          // NOTE: In many deployments user_roles.user_id is UNIQUE (1 role per user).
          // Keep only the first role to avoid unique-constraint violations.
          const safeRoles = Array.isArray(rolesData) ? rolesData.slice(0, 1) : [];
          const roleAssignments = safeRoles.map(role => ({
            user_id: userId,
            role_id: role.id,
            assigned_by: userData.teamLeaderId || userId
          }));

          const { error: assignError } = await supabase.rpc('admin_assign_roles_to_user', {
            p_user_id: userId,
            // RPC expects jsonb, pass array directly (do NOT JSON.stringify)
            p_role_assignments: roleAssignments
          });
          if (assignError) throw assignError;
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating user:', error);
      return { success: false, error: error.message };
    }
  }

  static async deleteUser(userId) {
    try {
      await supabase.rpc('admin_update_user', {
        p_user_id: userId,
        p_status: 'inactive'
      });
      return { success: true };
    } catch (error) {
      console.error('Error deleting user:', error);
      return { success: false, error: error.message };
    }
  }

  static async resetUserPassword(email, newPassword) {
    try {
      if (!email || !newPassword) {
        return { success: false, error: 'Email and new password are required' };
      }
      if (newPassword.length < 6) {
        return { success: false, error: 'Password must be at least 6 characters' };
      }

      const cleanEmail = email.trim().toLowerCase();
      const cleanPassword = newPassword.trim();

      // Must use bcrypt RPC — same as staff login (login_verify_user + admin_create_user).
      // update_user_password on older DBs writes md5_* hashes and breaks login.
      const { data, error } = await supabase.rpc('admin_reset_user_password', {
        p_email: cleanEmail,
        p_new_password: cleanPassword,
      });

      if (error) {
        if (error.code === 'PGRST202') {
          return {
            success: false,
            error: 'Password reset RPC not deployed. Run web-app/database_migrations/admin_reset_user_password_bcrypt.sql in Supabase SQL Editor.',
          };
        }
        throw error;
      }

      const result = Array.isArray(data) ? data[0] : data;
      if (result?.success) {
        return { success: true, message: result.message || 'Password reset successfully' };
      }

      return { success: false, error: result?.message || 'Failed to reset password' };
    } catch (error) {
      console.error('Error resetting user password:', error);
      return { success: false, error: error.message || 'Failed to reset password' };
    }
  }

  static async changeTeamLead(telecallerId, newTeamLeadId, changedBy) {
    try {
      // DB function signatures vary across deployments; try most specific first.
      // Canonical (from repo SQL): admin_change_team_lead(telecaller_id, new_team_lead_id, changed_by, old_team_lead_id default null)
      const currentUser = (() => {
        try {
          return JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
        } catch {
          return {};
        }
      })();
      const changedByUuid = changedBy && String(changedBy).length > 20 ? changedBy : currentUser?.id || null;

      const attempts = [
        { telecaller_id: telecallerId, new_team_lead_id: newTeamLeadId, changed_by: changedByUuid, old_team_lead_id: null },
        { telecaller_id: telecallerId, new_team_lead_id: newTeamLeadId, old_team_lead_id: null },
        // legacy param names (older frontends)
        { p_telecaller_id: telecallerId, p_new_team_lead_id: newTeamLeadId, p_changed_by: changedByUuid },
      ];

      let lastError = null;
      for (const args of attempts) {
        const { error } = await supabase.rpc('admin_change_team_lead', args);
        if (!error) {
          return { success: true };
        }
        lastError = error;
        // stop early if it's not a signature mismatch
        if (error.code && error.code !== 'PGRST202') break;
      }

      if (lastError) throw lastError;
      return { success: true };
    } catch (error) {
      console.error('Error changing team lead:', error);
      return { success: false, error: error.message };
    }
  }

  static async getMechanicCoverageByState() {
    try {
      const { data, error } = await supabase.rpc('get_mechanic_coverage_by_state');
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting mechanic coverage:', error);
      return [];
    }
  }

  static async getUniqueMechanicPincodeCount() {
    try {
      const { data, error } = await supabase.rpc('get_unique_mechanic_pincode_count');
      if (error) throw error;
      return data?.[0]?.count || 0;
    } catch (error) {
      console.error('Error getting unique pincode count:', error);
      return 0;
    }
  }

  // Payout Requests (Admin)
  static async getPayoutRequests(status = null) {
    try {
      const { data, error } = await supabase.rpc('admin_list_payout_requests', {
        p_status: status,
      });
      if (error) {
        console.error('Error getting payout requests:', error);
        return [];
      }
      return data || [];
    } catch (error) {
      console.error('Error getting payout requests:', error);
      return [];
    }
  }

  static async getPayoutRequestItems(requestId) {
    try {
      const { data, error } = await supabase.rpc('admin_get_payout_request_items', {
        p_request_id: requestId,
      });
      if (error) {
        console.error('Error getting payout request items:', error);
        return [];
      }
      return data || [];
    } catch (error) {
      console.error('Error getting payout request items:', error);
      return [];
    }
  }

  static async updatePayoutRequest(requestId, status, note = null) {
    try {
      const { data, error } = await supabase.rpc('admin_update_payout_request', {
        p_request_id: requestId,
        p_status: status,
        p_note: note,
      });
      if (error) throw error;
      return { success: true, data: data?.[0] || null };
    } catch (error) {
      console.error('Error updating payout request:', error);
      return { success: false, error: error.message || 'Failed to update payout request' };
    }
  }

  // --- Call provider & DID (via SECURITY DEFINER RPCs) ---

  static _getAdminUserId() {
    try {
      const u = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
      return u.id || null;
    } catch { return null; }
  }

  static async getCallProviders() {
    const { data, error } = await supabase.rpc('admin_get_call_providers');
    if (error) {
      console.error('getCallProviders:', error);
      return [];
    }
    return data || [];
  }

  static async upsertCallProvider(row) {
    const uid = this._getAdminUserId();
    if (!uid) return { ok: false, error: 'Not logged in' };
    const { data, error } = await supabase.rpc('admin_upsert_call_provider', {
      p_user_id: uid,
      p_id: row.id ?? null,
      p_name: row.name,
      p_display_name: row.display_name,
      p_api_base_url: row.api_base_url ?? '',
      p_api_endpoint: row.api_endpoint ?? '',
      p_http_method: row.http_method ?? 'POST',
      p_auth_type: row.auth_type ?? 'query',
      p_auth_creds: row.auth_credentials ?? {},
      p_request_map: row.request_mapping ?? {},
      p_resp_path: row.response_success_path ?? null,
      p_has_did: row.has_did_support ?? false,
      p_is_enabled: row.is_enabled ?? true,
      p_priority: row.priority ?? 10,
    });
    if (error) return { ok: false, error: error.message };
    return data || { ok: true };
  }

  static async deleteCallProvider(id) {
    const uid = this._getAdminUserId();
    if (!uid) return { ok: false, error: 'Not logged in' };
    const { data, error } = await supabase.rpc('admin_delete_call_provider', { p_user_id: uid, p_id: id });
    if (error) return { ok: false, error: error.message };
    return data || { ok: true };
  }

  static async updateCallProviderPriority(id, priority) {
    const uid = this._getAdminUserId();
    if (!uid) return { ok: false, error: 'Not logged in' };
    const { data, error } = await supabase.rpc('admin_update_call_provider_priority', {
      p_user_id: uid, p_id: id, p_priority: priority,
    });
    if (error) return { ok: false, error: error.message };
    return data || { ok: true };
  }

  static async getCallProviderConfig() {
    const { data, error } = await supabase.rpc('admin_get_call_provider_config');
    if (error) {
      console.error('getCallProviderConfig:', error);
      return null;
    }
    const rows = data || [];
    return rows.length > 0 ? rows[0] : null;
  }

  static async updateCallProviderConfig(patch) {
    const uid = this._getAdminUserId();
    if (!uid) return { ok: false, error: 'Not logged in' };
    const { data, error } = await supabase.rpc('admin_update_call_provider_config', {
      p_user_id: uid,
      p_fallback_enabled: patch.fallback_enabled ?? null,
      p_did_rotation_mode: patch.smartflo_did_rotation_mode ?? null,
    });
    if (error) return { ok: false, error: error.message };
    return data || { ok: true };
  }

  static async getProviderDids(providerId) {
    const { data, error } = await supabase.rpc('admin_get_provider_dids', { p_provider_id: providerId });
    if (error) {
      console.error('getProviderDids:', error);
      return [];
    }
    return data || [];
  }

  static async upsertDid(row) {
    const uid = this._getAdminUserId();
    if (!uid) return { ok: false, error: 'Not logged in' };
    const { data, error } = await supabase.rpc('admin_upsert_did', {
      p_user_id: uid,
      p_id: row.id ?? null,
      p_provider_id: row.provider_id,
      p_did_number: row.did_number,
      p_label: row.label ?? null,
      p_is_active: row.is_active ?? true,
      p_priority: row.priority ?? 1,
    });
    if (error) return { ok: false, error: error.message };
    return data || { ok: true };
  }

  static async deleteDid(id) {
    const uid = this._getAdminUserId();
    if (!uid) return { ok: false, error: 'Not logged in' };
    const { data, error } = await supabase.rpc('admin_delete_did', { p_user_id: uid, p_id: id });
    if (error) return { ok: false, error: error.message };
    return data || { ok: true };
  }

  static async getAgentDidAssignments() {
    const { data, error } = await supabase.rpc('admin_get_agent_did_assignments');
    if (error) {
      console.error('getAgentDidAssignments:', error);
      return [];
    }
    return data || [];
  }

  static async upsertAgentDidAssignment(row) {
    const uid = this._getAdminUserId();
    if (!uid) return { ok: false, error: 'Not logged in' };
    const { data, error } = await supabase.rpc('admin_upsert_agent_did_assignment', {
      p_user_id: uid,
      p_agent_phone: row.agent_phone,
      p_agent_name: row.agent_name ?? null,
      p_did_id: row.did_id ?? null,
      p_is_active: row.is_active ?? true,
    });
    if (error) return { ok: false, error: error.message };
    return data || { ok: true };
  }

  static async getAppEnquiries(type, { search = '', limit = 50, offset = 0 } = {}) {
    try {
      const { data, error } = await supabase.rpc('admin_list_app_enquiries', {
        p_type: type,
        p_limit: limit,
        p_offset: offset,
        p_search: search || null,
      });
      if (error) {
        console.error('getAppEnquiries:', error);
        return { success: false, rows: [], total: 0, error: error.message };
      }
      return {
        success: data?.success !== false,
        rows: data?.rows || [],
        total: Number(data?.total) || 0,
        error: data?.error || null,
      };
    } catch (error) {
      console.error('getAppEnquiries:', error);
      return { success: false, rows: [], total: 0, error: error.message };
    }
  }

  static async updateWhatsAppDraftStatus(draftId, status) {
    try {
      const { data, error } = await supabase.rpc('admin_update_whatsapp_draft_status', {
        p_draft_id: draftId,
        p_status: status,
      });
      if (error) {
        console.error('updateWhatsAppDraftStatus:', error);
        return { success: false, error: error.message };
      }
      return {
        success: data?.success !== false,
        error: data?.error || null,
        data,
      };
    } catch (error) {
      console.error('updateWhatsAppDraftStatus:', error);
      return { success: false, error: error.message };
    }
  }

  static async updateRsaEnquiryStatus(requestId, action, note = null) {
    try {
      const { data, error } = await supabase.rpc('admin_update_rsa_enquiry_status', {
        p_request_id: requestId,
        p_action: action,
        p_note: note || null,
      });
      if (error) {
        console.error('updateRsaEnquiryStatus:', error);
        return { success: false, error: error.message };
      }
      return {
        success: data?.success !== false,
        error: data?.error || null,
        data,
      };
    } catch (error) {
      console.error('updateRsaEnquiryStatus:', error);
      return { success: false, error: error.message };
    }
  }

  /** Admin / RoadServe official rate — customer RSA, WhatsApp, and B2B jobs */
  static async notifyWhatsAppAdminQuote(requestId, amountHint = null) {
    const id = String(requestId || '').trim();
    if (!id) return;
    const headers = {
      'Content-Type': 'application/json',
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    };
    try {
      const [{ data: req }, { data: sess }] = await Promise.all([
        supabase
          .from('service_requests')
          .select(
            'source, contact_number, service_type, problem, admin_quoted_amount, customer_quoted_amount, request_status, customer_name',
          )
          .eq('id', id)
          .maybeSingle(),
        supabase
          .from('whatsapp_rsa_sessions')
          .select('contact_phone, account_id, status')
          .eq('service_request_id', id)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);
      const src = String(req?.source || '').toLowerCase();
      const isWa = src.includes('whatsapp') || !!sess;
      const closed = String(req?.request_status || '').toLowerCase();
      if (isWa && req && !['cancelled', 'canceled', 'completed', 'closed'].includes(closed)) {
        let phone = String(sess?.contact_phone || '').replace(/\D/g, '').slice(-10);
        if (phone.length < 10) {
          phone = String(req.contact_number || '').replace(/\D/g, '').slice(-10);
        }
        const amt = Number(amountHint || req.admin_quoted_amount || req.customer_quoted_amount);
        if (phone.length >= 10 && Number.isFinite(amt) && amt > 0) {
          const amountLabel = `₹${amt.toLocaleString('en-IN')}`;
          const svc = String(req.service_type || 'RSA').trim() || 'RSA';
          const problem = String(req.problem || '').trim();
          const extra = problem && problem.toLowerCase() !== svc.toLowerCase() ? `\n${problem}` : '';
          const message =
            `*RoadServe* official rate\n\n` +
            `Service: ${svc}${extra}\n` +
            `Amount: *${amountLabel}*\n\n` +
            `Accept karne ke liye *1* ya *YES* reply karein.\n` +
            `Yeh RoadServe ka rate hai.`;
          let accountId = sess?.account_id || 'ab37ce64-bdb9-46ff-b482-7f2af00b73d5';
          try {
            const { data: accounts } = await supabase
              .from('whatsapp_accounts_safe')
              .select('id, name, display_phone, is_active')
              .eq('is_active', true);
            const rows = Array.isArray(accounts) ? accounts : [];
            const digits = (v) => String(v || '').replace(/\D/g, '');
            const preferred =
              rows.find((a) => digits(a.display_phone).endsWith('7909999427')) ||
              rows.find((a) => String(a.name || '').trim() === 'RoadServe');
            if (preferred?.id) accountId = preferred.id;
          } catch {
            /* keep default RoadServe inbox */
          }
          await fetch(`${SUPABASE_URL}/functions/v1/whatsapp-send`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              to: `91${phone}`,
              type: 'text',
              content: message,
              contact_name: req.customer_name || 'Customer',
              user_id: ENV.WHATSAPP_SYSTEM_USER_ID || '879ce3a2-ad29-4953-996b-7d01b77d14d2',
              account_id: accountId,
              source: 'ai',
            }),
          });
          await supabase.from('rsa_job_messages').insert({
            service_request_id: id,
            mechanic_id: null,
            sender_user_id: ENV.WHATSAPP_SYSTEM_USER_ID || '879ce3a2-ad29-4953-996b-7d01b77d14d2',
            sender_role: 'staff',
            message: `RoadServe official rate ${amountLabel} WhatsApp par customer ko bhej diya.`,
            message_type: 'text',
          });
          await supabase
            .from('whatsapp_rsa_sessions')
            .update({
              status: 'awaiting_quote_choice',
              awaiting_quote_choice: true,
              updated_at: new Date().toISOString(),
            })
            .eq('service_request_id', id)
            .in('status', ['job_open', 'awaiting_quote_choice', 'collecting', 'card_pending_warn']);
        }
      }
    } catch (error) {
      console.warn('notifyWhatsAppAdminQuote send:', error);
    }
    try {
      await fetch(`${SUPABASE_URL}/functions/v1/whatsapp-rsa-notify`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ request_id: id, event: 'admin_quote' }),
      });
    } catch (error) {
      console.warn('notifyWhatsAppAdminQuote:', error);
    }
  }

  static async setRsaEnquiryRate(requestId, amount, note = null) {
    const rupees = Math.round(Number(amount) * 100) / 100;
    try {
      const { data, error } = await supabase.rpc('admin_set_rsa_enquiry_rate', {
        p_request_id: requestId,
        p_amount: rupees,
        p_note: note || null,
      });
      const rpcMsg = error?.message || data?.error || '';
      const rpcOk = !error && data?.success !== false;
      if (rpcOk) {
        void AdminService.notifyWhatsAppAdminQuote(requestId, rupees);
        return { success: true, error: null, data };
      }

      const allowFallback =
        /not found|could not find the function|schema cache|invalid input/i.test(rpcMsg);
      if (!allowFallback) {
        console.error('setRsaEnquiryRate:', error || data);
        return { success: false, error: rpcMsg || 'Failed to save rate', data };
      }

      const db = serviceRoleClient || supabase;
      const patch = {
        customer_quoted_amount: rupees,
        admin_quoted_amount: rupees,
        pricing_mode: 'negotiated',
        rate_status: 'admin_quoted',
        needs_admin_rate: false,
        open_for_partner_accept: true,
        partner_bell_seconds: 3600,
        partner_bell_started_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      if (note && String(note).trim()) {
        patch.complete_remark = String(note).trim().slice(0, 500);
      }
      const { data: updated, error: writeErr } = await db
        .from('service_requests')
        .update(patch)
        .eq('id', requestId)
        .select('id')
        .maybeSingle();
      if (writeErr) {
        console.error('setRsaEnquiryRate fallback:', writeErr);
        return { success: false, error: writeErr.message || rpcMsg };
      }
      if (!updated?.id) {
        return { success: false, error: rpcMsg || 'RSA enquiry not found' };
      }
      void AdminService.notifyWhatsAppAdminQuote(requestId, rupees);
      return {
        success: true,
        error: null,
        data: {
          success: true,
          request_id: requestId,
          customer_quoted_amount: rupees,
          admin_quoted_amount: rupees,
          rate_status: 'admin_quoted',
        },
      };
    } catch (error) {
      console.error('setRsaEnquiryRate:', error);
      return { success: false, error: error.message };
    }
  }

  static async updateCarLoanEnquiryStatus(leadId, action, note = null) {
    try {
      const { data, error } = await supabase.rpc('admin_update_car_loan_enquiry_status', {
        p_lead_id: leadId,
        p_action: action,
        p_note: note || null,
      });
      if (error) {
        console.error('updateCarLoanEnquiryStatus:', error);
        return { success: false, error: error.message };
      }
      return {
        success: data?.success !== false,
        error: data?.error || null,
        data,
      };
    } catch (error) {
      console.error('updateCarLoanEnquiryStatus:', error);
      return { success: false, error: error.message };
    }
  }

  static async updateCarServiceEnquiryStatus(bookingId, action, note = null) {
    try {
      const { data, error } = await supabase.rpc('admin_update_car_service_enquiry_status', {
        p_booking_id: bookingId,
        p_action: action,
        p_note: note || null,
      });
      if (error) {
        console.error('updateCarServiceEnquiryStatus:', error);
        return { success: false, error: error.message };
      }
      return {
        success: data?.success !== false,
        error: data?.error || null,
        data,
      };
    } catch (error) {
      console.error('updateCarServiceEnquiryStatus:', error);
      return { success: false, error: error.message };
    }
  }

  static async getCustomerDetail(userId) {
    try {
      const { data, error } = await supabase.rpc('admin_get_customer_detail', {
        p_user_id: userId,
      });
      if (!error && data?.success) return data;
      if (error) console.warn('admin_get_customer_detail RPC failed, using fallback:', error.message);
    } catch (e) {
      console.warn('admin_get_customer_detail RPC error:', e.message);
    }

    try {
      const client = serviceRoleClient;
      const [{ data: user, error: userError }, { data: roleRows }, { data: customers }, { data: vehicles }, { data: subs }] =
        await Promise.all([
          client.from('users').select('id, name, email, phone, status, created_at, last_login, avatar_url, type').eq('id', userId).maybeSingle(),
          client.from('user_roles').select('roles(name)').eq('user_id', userId),
          client.from('customers').select('*').eq('user_id', userId).limit(1),
          client.from('customer_vehicles').select('*').eq('customer_id', userId),
          client
            .from('customer_membership_subscriptions')
            .select('*, plan:membership_plans(id, name, slug, price, duration_months, services_per_year), vehicle:customer_vehicles(id, registration_number, brand, model)')
            .eq('customer_id', userId)
            .order('created_at', { ascending: false }),
        ]);

      if (userError || !user) {
        return { success: false, error: userError?.message || 'User not found' };
      }

      const roles = (roleRows || [])
        .map((r) => r.roles?.name)
        .filter(Boolean)
        .map((n) => String(n).toLowerCase());

      const subscriptions = (subs || []).map((s) => ({
        ...s,
        plan_name: s.plan?.name || null,
        plan_slug: s.plan?.slug || null,
        plan_price: s.plan?.price ?? null,
        plan_duration_months: s.plan?.duration_months ?? null,
        plan_services_per_year: s.plan?.services_per_year ?? null,
        vehicle_registration: s.vehicle?.registration_number || null,
        vehicle_model: s.vehicle?.model || null,
        vehicle_brand: s.vehicle?.brand || null,
      }));

      return {
        success: true,
        user,
        roles,
        customer: customers?.[0] || null,
        vehicles: vehicles || [],
        subscriptions,
      };
    } catch (error) {
      console.error('getCustomerDetail fallback:', error);
      return { success: false, error: error.message || 'Failed to load customer' };
    }
  }

  static async listMembershipPlansForAssign() {
    try {
      const { data, error } = await supabase
        .from('membership_plans')
        .select('id, name, slug, price, duration_months, billing_type, services_per_year, is_active, position')
        .order('position', { ascending: false });
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('listMembershipPlansForAssign:', error);
      return [];
    }
  }

  static async assignCustomerPlan({
    customerId,
    planId,
    vehicleId = null,
    subscriptionId = null,
    action = 'assign',
    startedAt = null,
    expiresAt = null,
    amountPaid = null,
    servicesUsed = null,
    note = null,
  }) {
    try {
      const { data, error } = await supabase.rpc('admin_assign_customer_plan', {
        p_customer_id: customerId,
        p_plan_id: planId,
        p_vehicle_id: vehicleId,
        p_subscription_id: subscriptionId,
        p_action: action,
        p_started_at: startedAt,
        p_expires_at: expiresAt,
        p_amount_paid: amountPaid,
        p_services_used: servicesUsed,
        p_note: note,
      });
      if (error) {
        console.error('assignCustomerPlan:', error);
        return { success: false, error: error.message };
      }
      if (data?.success === false) {
        return { success: false, error: data.error || 'Plan update failed' };
      }
      return { success: true, data };
    } catch (error) {
      console.error('assignCustomerPlan:', error);
      return { success: false, error: error.message || 'Plan update failed' };
    }
  }

  static async cancelCustomerPlan(customerId, subscriptionId) {
    try {
      const { data, error } = await supabase.rpc('admin_assign_customer_plan', {
        p_customer_id: customerId,
        p_plan_id: null,
        p_vehicle_id: null,
        p_subscription_id: subscriptionId,
        p_action: 'cancel',
      });
      if (error) {
        console.error('cancelCustomerPlan:', error);
        return { success: false, error: error.message };
      }
      if (data?.success === false) {
        return { success: false, error: data.error || 'Cancel failed' };
      }
      return { success: true, data };
    } catch (error) {
      console.error('cancelCustomerPlan:', error);
      return { success: false, error: error.message || 'Cancel failed' };
    }
  }
}
